import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, Button, Card, ActiveToggle, Field, PillButton, apiErrorMessage, formatMoney, brandDisplayName } from '@aralere/shared-ui';
import type { Vendor, CreateVendorRequest, RemittanceBrandBalances, VendorDeactivationWarning } from '@aralere/shared-types';

// Vendors admin (spec §10.1) — the first Stage-3 slice. Global reference data: create / edit, plus
// the forward-only `active` visibility toggle (an inactive vendor is hidden from NEW invoice pickers
// going forward and never affects existing records). No delete (soft-visibility only), no logo yet.

interface FormState {
  displayName: string;
  companyNumber: string;
  companyEmail: string;
  billingEmail: string;
  website: string;
  repName: string;
  repEmail: string;
  repNumber: string;
  isSubcontractor: boolean;
}

const emptyForm = (): FormState => ({
  displayName: '',
  companyNumber: '',
  companyEmail: '',
  billingEmail: '',
  website: '',
  repName: '',
  repEmail: '',
  repNumber: '',
  isSubcontractor: false,
});

const formFromVendor = (v: Vendor): FormState => ({
  displayName: v.displayName,
  companyNumber: formatPhone(v.companyNumber ?? ''),
  companyEmail: v.companyEmail ?? '',
  billingEmail: v.billingEmail ?? '',
  website: v.website ?? '',
  repName: v.repName ?? '',
  repEmail: v.repEmail ?? '',
  repNumber: formatPhone(v.repNumber ?? ''),
  isSubcontractor: v.isSubcontractor,
});

const buildPayload = (f: FormState): CreateVendorRequest => ({
  displayName: f.displayName.trim(),
  companyNumber: f.companyNumber,
  companyEmail: f.companyEmail,
  billingEmail: f.billingEmail,
  website: f.website,
  repName: f.repName,
  repEmail: f.repEmail,
  repNumber: f.repNumber,
  isSubcontractor: f.isSubcontractor,
});

export function AdminVendors(): JSX.Element {
  const [vendors, setVendors] = useState<Vendor[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  /** 04 #125 WS-B (02g AMENDMENT (C)): the "Collected on behalf" block of the vendor being edited, per
   *  brand, from the ONE balances derivation. null = not loaded or not a payee of any class. */
  const [owedBlock, setOwedBlock] = useState<RemittanceBrandBalances[] | null>(null);
  /** 04 #125 rule 7: a deactivation with an open balance WARNS (never blocks); shown here, amber. */
  const [warnings, setWarnings] = useState<VendorDeactivationWarning[]>([]);

  const load = async (): Promise<void> => {
    try {
      const res = await api.admin.listVendors();
      setVendors(res.vendors);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const update = (patch: Partial<FormState>): void => setForm((f) => ({ ...f, ...patch }));

  const openCreate = (): void => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
    setFormOpen(true);
  };

  const openEdit = (v: Vendor): void => {
    setEditingId(v.id);
    setForm(formFromVendor(v));
    setFormError(null);
    setFormOpen(true);
    setOwedBlock(null);
    // Best-effort: the block simply stays absent if the derivation is unreachable.
    void (async () => {
      try {
        const res = await api.admin.remittanceBalances({ payeeVendorId: v.id });
        setOwedBlock(res.brands.filter((b) => b.payees.length > 0));
      } catch {
        setOwedBlock(null);
      }
    })();
  };

  const closeForm = (): void => {
    setFormOpen(false);
    setEditingId(null);
    setFormError(null);
  };

  const submit = async (): Promise<void> => {
    if (form.displayName.trim() === '') {
      setFormError('A vendor display name is required.');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      if (editingId) {
        await api.admin.updateVendor(editingId, buildPayload(form));
      } else {
        await api.admin.createVendor(buildPayload(form));
      }
      await load();
      closeForm();
    } catch (err) {
      setFormError(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (v: Vendor): Promise<void> => {
    setBusyId(v.id);
    setError(null);
    setWarnings([]);
    try {
      const res = await api.admin.updateVendor(v.id, { active: !v.active });
      setWarnings(res.warnings ?? []);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Vendors</h1>
          <p className="text-sm text-[var(--color-muted)]">
            {vendors == null ? 'Loading…' : `${vendors.length} vendor${vendors.length === 1 ? '' : 's'} · includes inactive`}
          </p>
        </div>
        <Button type="button" onClick={openCreate}>New vendor</Button>
      </div>

      {error ? <p className="text-sm text-[var(--tint-red-text)]">{error}</p> : null}
      {warnings.length > 0 ? (
        <Card>
          <div className="space-y-1 text-sm text-[var(--tint-warn-text)]">
            {warnings.map((w) => (
              <p key={w.businessId}>
                {w.message}{' '}
                <Link className="underline decoration-dotted underline-offset-2 hover:decoration-solid" to={`/admin/remittances?business=${encodeURIComponent(w.businessId)}`}>Open remittances &rarr;</Link>
              </p>
            ))}
          </div>
        </Card>
      ) : null}

      {formOpen ? (
        <Card>
          <div className="space-y-4">
            <p className="text-lg font-medium">{editingId ? 'Edit vendor' : 'New vendor'}</p>
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Display name" required>
                <Input value={form.displayName} onChange={(v) => update({ displayName: v })} placeholder="Newegg" />
              </Field>
              <Field label="Company phone">
                <Input value={form.companyNumber} onChange={(v) => update({ companyNumber: formatPhone(v) })} placeholder="(555)-123-4567" />
              </Field>
              <Field label="Company email">
                <Input value={form.companyEmail} onChange={(v) => update({ companyEmail: v })} placeholder="orders@vendor.com" />
              </Field>
              <Field label="Billing email">
                <Input value={form.billingEmail} onChange={(v) => update({ billingEmail: v })} placeholder="billing@vendor.com" />
              </Field>
              <Field label="Website">
                <Input value={form.website} onChange={(v) => update({ website: v })} placeholder="https://vendor.com" />
              </Field>
              <Field label="Representative name">
                <Input value={form.repName} onChange={(v) => update({ repName: v })} placeholder="Alex Rep" />
              </Field>
              <Field label="Representative email">
                <Input value={form.repEmail} onChange={(v) => update({ repEmail: v })} placeholder="alex@vendor.com" />
              </Field>
              <Field label="Representative phone">
                <Input value={form.repNumber} onChange={(v) => update({ repNumber: formatPhone(v) })} placeholder="(555)-123-4567" />
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isSubcontractor}
                onChange={(e) => update({ isSubcontractor: e.target.checked })}
              />
              This vendor is a subcontractor (its costs show to clients as "Additional labor costs")
            </label>
            {/* 04 #125 WS-B: what this vendor is owed as the payee of pass-through classes, per brand; the
                brand name collapses when the instance has one brand (the expenses-panel convention). */}
            {editingId && owedBlock && owedBlock.length > 0 ? (
              <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] p-3 text-sm">
                <p className="font-medium">Collected on behalf</p>
                <div className="mt-1 space-y-0.5 text-[var(--color-muted)]">
                  {owedBlock.map((b) => {
                    const owed = b.payees.reduce((s, p) => s + p.owed, 0);
                    return (
                      <div key={b.businessId} className="flex flex-wrap items-center gap-x-2">
                        {owedBlock.length > 1 ? <span className="text-[var(--color-text)]">{brandDisplayName(b.businessName)}</span> : null}
                        <span className={owed < 0 ? 'text-[var(--tint-red-text)]' : ''}>
                          {owed < 0 ? `over-paid by ${formatMoney(-owed)}` : `${formatMoney(owed)} owed`}
                        </span>
                        <span aria-hidden="true">&middot;</span>
                        <Link className="underline decoration-dotted underline-offset-2 hover:decoration-solid" to={`/admin/remittances?business=${encodeURIComponent(b.businessId)}`}>View remittances &rarr;</Link>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
            {formError ? <p className="text-sm text-[var(--tint-red-text)]">{formError}</p> : null}
            <div className="flex gap-2">
              <Button type="button" onClick={() => void submit()} disabled={submitting}>
                {submitting ? 'Saving…' : editingId ? 'Save changes' : 'Create vendor'}
              </Button>
              <Button type="button" variant="ghost" onClick={closeForm} disabled={submitting}>
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      <Card>
        {vendors == null ? (
          <p className="text-sm text-[var(--color-muted)]">Loading…</p>
        ) : vendors.length === 0 ? (
          <p className="text-sm text-[var(--color-muted)]">No vendors yet. Add one to log expenses against it.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[var(--color-muted)]">
                <th className="px-3 py-2 font-medium">Vendor</th>
                <th className="px-3 py-2 font-medium">Contact</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Visible</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((v) => (
                <tr key={v.id} className="border-t border-[rgb(var(--edge-rgb)/0.05)]">
                  <td className="px-3 py-3">
                    <div className="font-medium">{v.displayName}</div>
                    {v.companyNumber ? <div className="text-xs text-[var(--color-muted)]">{v.companyNumber}</div> : null}
                  </td>
                  <td className="px-3 py-3 text-[var(--color-muted)]">
                    <div>{v.repName || '-'}</div>
                    <div className="text-xs">{v.repNumber || '-'}</div>
                    <div className="text-xs">{v.repEmail || v.billingEmail || v.companyEmail || '-'}</div>
                  </td>
                  <td className="px-3 py-3 text-[var(--color-muted)]">
                    {v.isSubcontractor ? 'Subcontractor' : 'Vendor'}
                  </td>
                  <td className="px-3 py-3">
                    <ActiveToggle active={v.active} busy={busyId === v.id} label={v.displayName} onToggle={() => toggleActive(v)} />
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex justify-end">
                      <PillButton onClick={() => openEdit(v)}>
                        Edit
                      </PillButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

/**
 * Progressive US phone formatting to (XXX)-XXX-XXXX as the user types. Strips all non-digits, caps at
 * 10, and reformats each keystroke — so backspace and mid-string edits reflow cleanly. Applied to the
 * company + representative phone fields; the stored value is the formatted string.
 */
function formatPhone(input: string): string {
  const d = input.replace(/\D/g, '').slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)})-${d.slice(3)}`;
  return `(${d.slice(0, 3)})-${d.slice(3, 6)}-${d.slice(6)}`;
}


function Input({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}): JSX.Element {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
    />
  );
}
